<!--

============================================================================================================
||    InfoCard2 component                                                                                 ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
  Component renders a card with an image, title, subtitle and an action button.

|   Features
------------------------------------------------------------------------------------------------------------
  - Cover icon/image
  - Title
  - Subtitle
  - Action button - customizable via slot

|   Props
------------------------------------------------------------------------------------------------------------
  - icon              : Object    : Icon object from SBIconList.
  - image             : String    : Image URL. Overrides icon if both are provided.
  - title             : String    : Title of the card.
  - subtitle          : String    : Subtitle of the card.
  - noAction          : Boolean   : If true, action button is not rendered.
  - actionIcon        : Object    : Icon object from SBIconList.
  - actionText        : String    : Text for the action button.
  - backgroundColor1  : String    : Background color for the card.
  - backgroundColor2  : String    : Background color for the card.

|   Events
------------------------------------------------------------------------------------------------------------
  - action - Emits when action button is clicked.

|   Slots
------------------------------------------------------------------------------------------------------------
  - action-slot - Slot for custom action button.

|   TODO
------------------------------------------------------------------------------------------------------------
  - For rewrite take only InfoCard or InfoCard2 and remove the other one. Also mix it if needed to have only one component.

-->

<template>
  <div
    class="info-card-wrapper flex justify-center"
    :style="cssVars" >
    <div
      class="info-card flex center-all w-100"
      :class="{ 'column': !inLine, 'reverse': reverse }"
      :style="{ 'flex-direction': reverse ? (inLine ? 'row-reverse' : 'column-reverse') : (inLine ? 'row' : 'column'), 'gap': inLine ? '20px' : '59px' }" >
      <div
        v-if="image || icon"
        class="flex center-all w-100" >
        <!-- Image -->
        <img
          v-if="image"
          :src="image"
          :class="{ 'mb-0': inLine }" >

        <!-- ICON -->
        <SBIcon
          v-else-if="icon"
          class="header-icon"
          :icon="icon"
          :color="iconColor"
          :size="36"/>
      </div>

      <div class="w-100">
        <!-- TITLE -->
        <h5 v-if="title" >{{ title }}</h5>

        <!-- SUBTITLE -->
        <!-- No user input is rendered here. -->
        <div
          v-if="subtitle"
          v-html-safe="subtitle"
          class="subtitle pre-lined"
          :style="{ 'padding': inLine ? '0 25px' : '0' }" />

        <!-- ACTION -->
        <div
          v-if="!noAction"
          class="action">
          <slot name="action-slot">
            <b-button
              class="flex center-all"
              @click="triggerAction">
              <SBIcon
                class="mr-2"
                :icon="actionIcon"
                :color="SBColors.blackPrimary"
                :size="8"/>
              <span>{{ actionText }}</span>
            </b-button>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';

export default {
  name: 'InfoCard2',
  components: {
    SBIcon,
  },
  props: {
    icon: {
      type: Object,
      required: false,
      default: null,
    },
    image: {
      type: String,
      required: false,
      default: null,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: false,
      default: null,
    },
    noAction: {
      type: Boolean,
      required: false,
      default: false,
    },
    actionIcon: {
      type: Object,
      required: false,
      default: () => SBIconList.PLUS,
    },
    actionText: {
      type: String,
      required: false,
      default: 'Create',
    },
    backgroundColor1: {
      type: String,
      required: false,
      default: SBColors.white,
    },
    backgroundColor2: {
      type: String,
      required: false,
      default: null,
    },
    maxWidth: {
      type: [Number, String],
      required: false,
      default: 570,
    },
    inLine: {
      type: Boolean,
      required: false,
      default: false,
    },
    reverse: {
      type: Boolean,
      required: false,
      default: false,
    },
    padding: {
      type: String,
      required: false,
      default: '72px 66px',
    },
  },
  data() {
    return {
      SBColors,
      SBIconList,
    };
  },
  computed: {
    cssVars() {
      return {
        '--background-color1': this.backgroundColor1,
        '--background-color2': this.backgroundColor2 ? this.backgroundColor2 : this.backgroundColor1,
        '--max-width': `${this.maxWidth}px`,
        '--padding': this.padding,
      };
    },
  },
  methods: {
    triggerAction() {
      this.$emit('action');
    },
  },
};
</script>

<style lang="scss" scoped>
.info-card-wrapper {
  margin: 96px 46px;

  .info-card {
    max-width: var(--max-width);
    padding: var(--padding);
    border: 1px solid #e1e5eb;
    border-radius: 15px;
    background: linear-gradient(to bottom, var(--background-color1), var(--background-color2));

    .flex > img {
      width: 100%;
      // margin-bottom: 59px;
    }

    .header-icon {
      margin-bottom: 15px;

      &.flat-icon {
        width: 36px;
        height: 36px;
        background-position: center!important;
        background-repeat: no-repeat !important;
        background-size: contain !important;
      }
    }

    h5 {
      color: $blackPrimary;
      font-size: 18px;
      font-weight: 600;
      text-align: center;
      margin-bottom: 10px;
    }

    .subtitle {
      color: $blackLight;
      font-size: 16px;
      font-weight: 400;
      margin-bottom: 45px;
      text-align: center;
      max-width: 474px;
      margin-left: auto;
      margin-right: auto;

      ::v-deep a {
        color: $blackLight;
        text-decoration: underline;
      }
    }

    .action {
      display: flex;
      justify-content: center;
      margin-bottom: 10px;
    }

    :last-child {
      margin-bottom: 0;
    }
  }
}
</style>
